/**
 * AetherLite Auth endpoints.
 *
 * El backend (aethercoach-mvp) expone los endpoints bajo /aetherlite/auth/*:
 *   - POST /aetherlite/auth/google     → recibe Google id_token, devuelve JWT 30d + user
 *   - GET  /aetherlite/auth/me         → user actual
 *   - PUT  /aetherlite/auth/me         → actualizar perfil (PUT, no PATCH)
 *   - GET  /aetherlite/auth/me/usage   → resumen de uso/tier para sidebar
 */
import { apiFetch, setToken, clearTokens } from "./client";
import type { StylePreset } from "../types/plan";

/** User shape devuelto por el backend AetherLite. Snake_case fiel al SQL. */
export interface AetherLiteUser {
  id: number;
  email: string;
  name: string;
  picture_url: string | null;
  // Branding del entrenador
  tagline: string | null;
  instagram: string | null;
  web: string | null;
  phone: string | null;
  brand_logo_url: string | null;
  brand_color_primary: string | null;
  brand_color_accent: string | null;
  brand_letters: string | null;
  // Defaults de portada (Feature 2) — se precargan en cada plan nuevo
  cover_eyebrow: string | null;
  cover_subtitle: string | null;
  cover_duration: string | null;
  // Presets de estilo guardados del coach (Feature 3)
  style_presets?: StylePreset[];
  // Pricing / admin
  plan_tier: "free" | "pro" | "studio" | string;
  is_admin: boolean;
  // Timestamps (opcionales — no siempre vienen del backend)
  created_at?: string;
  updated_at?: string;
}

export interface UsageSummary {
  plan_tier: string;
  plan_label: string;
  price_cop_monthly: number;
  clients_active: number;
  clients_limit: number;       // valor numérico (99999 cuando is_unlimited)
  is_unlimited?: boolean;      // backend devuelve true en tier Unlimited
  watermark: boolean;
}

export interface GoogleLoginResponse {
  access_token: string;
  token_type: string;
  user: AetherLiteUser;
}

/** Intercambia el id_token de Google por un JWT propio + user. Persiste el JWT. */
export async function loginGoogle(idToken: string): Promise<GoogleLoginResponse> {
  const res = await apiFetch<GoogleLoginResponse>("/aetherlite/auth/google", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken }),
  });
  if (res.access_token) setToken(res.access_token);
  return res;
}

/** Login con email + password — exclusivo para admins (staff Nilabs).
 *  Los entrenadores siguen usando Google. Devuelve 401 si las credenciales
 *  no matchean o si el user existe pero no es admin (para no filtrar quién
 *  tiene flag admin). */
export async function loginWithPassword(email: string, password: string): Promise<GoogleLoginResponse> {
  const res = await apiFetch<GoogleLoginResponse>("/aetherlite/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (res.access_token) setToken(res.access_token);
  return res;
}

export async function getMe(): Promise<AetherLiteUser> {
  return apiFetch<AetherLiteUser>("/aetherlite/auth/me");
}

/** Update parcial. Backend acepta PUT con campos parciales. */
export async function updateMe(data: Partial<AetherLiteUser>): Promise<AetherLiteUser> {
  return apiFetch<AetherLiteUser>("/aetherlite/auth/me", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getUsage(): Promise<UsageSummary> {
  return apiFetch<UsageSummary>("/aetherlite/auth/me/usage");
}

/** Resultado de "Aplicar mi marca a planes existentes". */
export interface ApplyBrandResult {
  updated_count: number;
  updated_plan_ids: number[];
  mode: "fill_empty" | "override";
}

/** Aplica el brand actual del coach a todos sus planes (o a un subset).
 *
 *  `mode = "fill_empty"` (default): solo escribe campos vacíos del brand del plan.
 *                                    Seguro — no pisa branding personalizado por plan.
 *  `mode = "override"`: reemplaza los campos identitarios del brand (preserva estilo
 *                       editorial: theme, fuentes, densidad). Útil cuando el coach
 *                       cambió logo/colores y quiere actualizar todos los planes.
 */
export async function applyBrandToPlans(
  mode: "fill_empty" | "override" = "fill_empty",
  planIds?: number[],
): Promise<ApplyBrandResult> {
  return apiFetch<ApplyBrandResult>("/aetherlite/auth/me/apply-brand-to-plans", {
    method: "POST",
    body: JSON.stringify({ mode, plan_ids: planIds }),
  });
}

/** Logout client-side. AetherLite no tiene endpoint /logout — el JWT es stateless,
 *  basta con borrarlo del localStorage para terminar la sesión. */
export function logout(): void {
  clearTokens();
}
